/**
 * Input — styled text input for the Abyss design system.
 * Frosted-glass surface, coral focus ring, and optional invalid state.
 * Fully forwards all native `<input>` attributes.
 */
import { forwardRef, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** When true, shows a red focus ring to signal a validation error. */
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={clsx(
        // Base layout & shape
        'block w-full rounded-lg px-3 py-2 text-sm',
        // Surface
        'bg-surface-glass backdrop-blur-sm',
        'ring-1 ring-surface-stroke',
        // Text
        'text-text-1 placeholder:text-text-3',
        // Transitions
        'transition-shadow duration-150',
        // Normal focus
        !invalid &&
          'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-0',
        // Invalid focus
        invalid &&
          'ring-bad focus:outline-none focus:ring-2 focus:ring-bad focus:ring-offset-0',
        // Disabled
        'disabled:opacity-40 disabled:cursor-not-allowed',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
});
